import React, { useMemo, useState } from 'react'
import { useSelector } from '../../../store'
import {
  EulaType,
  FieldMetadata,
  FieldMetadataField,
} from '../../../modules/privateOffer/types'
import {
  PlanType,
  RadioButtonGroupMapping,
  RadioButtonType,
} from '@labrav/react-components'
import { MainTabsV2 } from '@labrav/react-components'
import { FormikProps, useFormikContext } from 'formik'
import { scrollspyTabs } from '@labrav/common'
import { useTheme } from '@mui/material/styles'
import { EulaSection } from '../../../common/components/EulaSection/EulaSection'
import { PrivateOfferCustomMetaData } from '../../PrivateOfferFormV2/AwsOffer/PrivateOfferCustomeMetaData/PrivateOfferCustomMetaData'
import { PrivateOfferStakeholders } from '../../PrivateOfferFormV2/AwsOffer/PrivateOfferStakeholders/PrivateOfferStakeholders'
import { defaultNow } from '../../../common/utils/dateTimeNow'

import { useStyles } from './ResaleAuthorizationFormContainer.styles'
import { AuthorizationDetails } from './AuthorizationDetails/AuthorizationDetails'
import { AdditionalPaymentConfiguration } from './AdditionalPaymentConfiguration/AdditionalPaymentConfiguration'
import { RenewalSectionReseller } from './RenewalSectionReseller/RenewalSectionCombinedForm'
import { ContractDurationReseller } from './ContractDurationCombinedForm/ContractDurationCombinedForm'
import { ResaleUsageDimensions } from './ResaleUsageDimensions/ResaleUsageDimensions'
import { ResaleProductDimensions } from './ProductDimensions/ResaleProductDimensions'
import { AuthorizationDuration } from './AuthorizationDuration/AuthorizationDuration'
import { ResaleInstallmentsSection } from './ResaleInstallmentsSection/ResaleInstallmentsSection'
import { eulaStandardContractUrl } from '../../../common/utils/constants'
import { ResaleAuthorizationWithErrors } from '../../../modules/resaleAuthorization/types'
import { DiscountType } from '../../../types/flyout'
import { FlyOutListingData } from '../../../types/flyout'
import { MarketplaceTemplateDataResponse } from '../../../modules/marketplaceMappingManager/types'
import type { ResaleAuthorizationFormContainerProps } from '../types'

export type { ResaleAuthorizationFormContainerProps }

export function ResaleAuthorizationFormContainer({
  offset,
  tabTop,
  externalBaseURL,
  product,
  users,
}: ResaleAuthorizationFormContainerProps) {
  const { classes } = useStyles({ offset })
  const theme = useTheme()
  const formikValues =
    useFormikContext() as FormikProps<ResaleAuthorizationWithErrors>
  const { values } = formikValues

  const { flyoutSellerInfo } = useSelector<{
    flyoutSellerInfo: FlyOutListingData
  }>(state => ({
    flyoutSellerInfo: state.flyoutSellerInfo,
  }))
  const isIndiaRegistered = flyoutSellerInfo?.countryRegistered === 'IN'
  const paymentConfigurationNoteDescription = isIndiaRegistered
    ? 'As this account is based in India, you can use either of USD or INR currency.'
    : ''
  const isSaasSubscription = product?.productType === PlanType.SAAS_SUBSCRIPTION
  const additionalUsageObjects = (values.dimensions || []).filter(
    ({ isAdditionalUsageAllowed }) => isAdditionalUsageAllowed
  )
  const usageDimensionLength = isSaasSubscription
    ? values.dimensions
    : additionalUsageObjects

  const templateData =
    useSelector<MarketplaceTemplateDataResponse | undefined>(
      state => state.marketplaceMappingManager?.templateData?.templateData
    ) || ({} as MarketplaceTemplateDataResponse)

  const fieldMetadata = useSelector<FieldMetadata | undefined>(
    state =>
      state.resaleAuthorizationsInCreation.currentResaleAuthorizationInCreation[
        product?.productId as string
      ]?.fieldMetadata
  )

  const isEndCustomerEulaFileHidden = (
    fieldMetadata?.endCustomerEulaFiles as FieldMetadataField
  )?.hidden
  const isEndCustomerEulaTypeHidden = (
    fieldMetadata?.endCustomerEulaType as FieldMetadataField
  )?.hidden

  const isEndCustomerEulaFileReadOnly = (
    fieldMetadata?.endCustomerEulaFiles as FieldMetadataField
  )?.readOnly
  const isEndCustomerEulaTypeReadOnly = (
    fieldMetadata?.endCustomerEulaType as FieldMetadataField
  )?.readOnly

  const isResellerLicenseAgreementFileReadOnly = (
    fieldMetadata?.resellerLicenseAgreementFiles as FieldMetadataField
  )?.readOnly

  const shouldRenderEndCustomerEulaSection = useMemo(
    () => !(isEndCustomerEulaFileHidden && isEndCustomerEulaTypeHidden),
    [isEndCustomerEulaFileHidden, isEndCustomerEulaTypeHidden]
  )

  const isResellerLicenseAgreementFileHidden = (
    fieldMetadata?.resellerLicenseAgreementFiles as FieldMetadataField
  )?.hidden
  const isResellerLicenseAgreementTypeHidden = (
    fieldMetadata?.resellerLicenseAgreementType as FieldMetadataField
  )?.hidden

  const shouldRenderResellerLicenseAgreementSection = useMemo(
    () =>
      !(
        isResellerLicenseAgreementFileHidden &&
        isResellerLicenseAgreementTypeHidden
      ),
    [isResellerLicenseAgreementFileHidden, isResellerLicenseAgreementTypeHidden]
  )

  const isAuthorizationDurationHidden = (
    fieldMetadata?.agreementDurationType as FieldMetadataField
  )?.hidden

  const isAuthorizationEndDateHidden = (
    fieldMetadata?.agreementExpirationDate as FieldMetadataField
  )?.hidden

  const shouldRenderAuthorizationDurationSection = useMemo(
    () => !(isAuthorizationDurationHidden && isAuthorizationEndDateHidden),
    [isAuthorizationDurationHidden, isAuthorizationEndDateHidden]
  )

  const fieldMetadataMetaData =
    templateData?.fieldMappings?.fields?.fieldsMapping?.Labra__Metadata_Hide__c
      ?.isHidden
  const fieldMetadataStakeholders =
    templateData?.fieldMappings?.fields?.fieldsMapping?.Labra__Stakeholders__c
      ?.isHidden

  const isContractDurationHidden = (
    fieldMetadata?.contractDurationInMonthsForEndCustomer as FieldMetadataField
  )?.hidden

  const isMaximumAllowedServiceDateHidden = (
    fieldMetadata?.maxAgreementStartDate as FieldMetadataField
  )?.hidden

  const shouldRenderContractDurationSection = useMemo(
    () => !(isContractDurationHidden && isMaximumAllowedServiceDateHidden),
    [isContractDurationHidden, isMaximumAllowedServiceDateHidden]
  )
  const [eulaErr, setEulaErr] = useState<string>('')
  const updateErr = (error: string) => {
    setEulaErr(error)
  }
  const eulaButtonOptionsV2: RadioButtonGroupMapping[] = [
    {
      key: EulaType.PUBLIC,
      label: 'Public offer legal terms',
      type: RadioButtonType.STANDARD,
      url: product?.eulaUrl || '',
    },
    {
      key: EulaType.STANDARD,
      label: 'Standard contract for AWS marketplace (SCMP)',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'SCMP is a standardized contract template intended to help streamline procurement and speed up transactions.',
      url: eulaStandardContractUrl,
    },
    {
      key: EulaType.CUSTOM,
      label: 'Custom EULA',
      type: RadioButtonType.STANDARD,
    },
  ]
  const eulaResaleButtonOptionsV2: RadioButtonGroupMapping[] = [
    {
      key: EulaType.STANDARD,
      label: 'Reseller Contract for AWS Marketplace (RCMP)',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'Use the standard template to define contractual terms between you and channel partners for this Reseller authorization.',
      url: eulaStandardContractUrl,
    },
    {
      key: EulaType.CUSTOM,
      label: 'Custom EULA',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'Upload your own agreement to define contractual terms for this Reseller authorization.',
    },
  ]

  const ContainerOfferTabs = [
    {
      key: 'offer-details',
      label: 'Authorization details',
      renderElement: (
        <div id={'offer-details'} className={classes.scrollTopMargin}>
          <AuthorizationDetails
            noteDescription={
              isIndiaRegistered
                ? "As this account is based in India, you can only target channel partners and buyers based in India. Buyers outside India won't be able to accept the channel partner private offers that you authorize."
                : ''
            }
            product={product}
          />
          <AdditionalPaymentConfiguration
            product={product}
            noteDescription={paymentConfigurationNoteDescription}
          />
          {product.productType !== PlanType.SAAS_SUBSCRIPTION &&
            shouldRenderContractDurationSection && (
              <ContractDurationReseller
                product={product}
                isExternal={!!externalBaseURL}
              />
            )}
        </div>
      ),
      //   isError: getIsErrorForOfferTab(
      //     'offer-details',
      //     formikValues,
      //     eulaErr,
      //     product?.productType
      //   ),
    },
    {
      key: 'offer-pricing',
      label: 'Pricing',
      renderElement: (
        <div id={'offer-pricing'} className={classes.scrollTopMargin}>
          {product.productType !== PlanType.SAAS_SUBSCRIPTION && (
            <ResaleProductDimensions
              product={product}
              productId={product.productId}
            />
          )}
          {values.discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS && (
            <ResaleInstallmentsSection />
          )}
          {usageDimensionLength && usageDimensionLength.length > 0 && (
            <ResaleUsageDimensions product={product} />
          )}
        </div>
      ),
      //   isError: getIsErrorForOfferTab(
      //     'offer-pricing',
      //     formikValues,
      //     eulaErr,
      //     product?.productType
      //   ),
    },
    {
      key: 'offer-other',
      label: 'Other',
      renderElement: (
        <div id={'offer-other'} className={classes.scrollTopMargin}>
          {shouldRenderAuthorizationDurationSection && (
            <AuthorizationDuration product={product} />
          )}
          <RenewalSectionReseller product={product} />

          {shouldRenderEndCustomerEulaSection && (
            <EulaSection
              defaultNow={defaultNow}
              hideSubHeading={true}
              formik={
                formikValues as unknown as FormikProps<Record<string, unknown>>
              }
              isMultiFileUpload={true}
              fileUploadDescription={
                'Upload a up to 5 PDF files. Drag and drop it here.'
              }
              fileUploadButtonText={'Upload'}
              fileUploadContainerTitle={
                'Upload a up to 5 PDF files. Drag and drop it here.'
              }
              quantity={5}
              eulaOptionsHeading={''}
              isOptional={values?.endCustomerEulaType !== EulaType.CUSTOM}
              eulaOptionSubHeading={''}
              eulaOptions={eulaButtonOptionsV2}
              fileSize={10}
              updateErr={updateErr}
              uploadError={
                eulaErr ||
                (formikValues?.touched.endCustomerEulaType
                  ? (formikValues?.errors?.endCustomerEulaFiles as string)
                  : '')
              }
              disableEulaFileUpload={isEndCustomerEulaFileReadOnly || false}
              showEulaFileUpload={
                values.endCustomerEulaType === EulaType.CUSTOM
              }
              disableEulaOptions={isEndCustomerEulaTypeReadOnly || false}
              showEulaOptions={true}
              eulaTypeKeyName="endCustomerEulaType"
              eulaFilesKeyName="endCustomerEulaFiles"
              showHelpIcon={false}
            />
          )}

          {shouldRenderResellerLicenseAgreementSection && (
            <EulaSection
              defaultNow={defaultNow}
              hideSubHeading={true}
              formik={
                formikValues as unknown as FormikProps<Record<string, unknown>>
              }
              isMultiFileUpload={true}
              fileUploadDescription={
                'Upload a up to 5 PDF files. Drag and drop it here.'
              }
              fileUploadButtonText={'Upload'}
              fileUploadContainerTitle={
                'Upload a up to 5 PDF files. Drag and drop it here.'
              }
              quantity={5}
              eulaOptionsHeading={''}
              isOptional={
                values?.resellerLicenseAgreementType !== EulaType.CUSTOM
              }
              eulaOptionSubHeading={''}
              eulaOptions={eulaResaleButtonOptionsV2}
              fileSize={10}
              updateErr={updateErr}
              uploadError={
                eulaErr ||
                (formikValues?.touched.resellerLicenseAgreementType
                  ? (formikValues?.errors
                      ?.resellerLicenseAgreementFiles as string)
                  : '')
              }
              disableEulaFileUpload={
                isResellerLicenseAgreementFileReadOnly || false
              }
              showEulaFileUpload={
                values.resellerLicenseAgreementType === EulaType.CUSTOM
              }
              disableEulaOptions={
                isResellerLicenseAgreementFileReadOnly || false
              }
              showEulaOptions={true}
              eulaTypeKeyName="resellerLicenseAgreementType"
              eulaFilesKeyName="resellerLicenseAgreementFiles"
              isOptionalSection={true}
              sectionHeading="Reseller agreement (EULA)"
              showHelpIcon={false}
            />
          )}
          {!fieldMetadataStakeholders && (
            <PrivateOfferStakeholders
              keyName={'agreementStakeholders'}
              showHelpIcon={false}
              hideStakeholders={!!externalBaseURL}
              users={users}
            />
          )}
          {!fieldMetadataMetaData && (
            <PrivateOfferCustomMetaData product={product} />
          )}
        </div>
      ),
      //   isError: getIsErrorForOfferTab(
      //     'offer-other',
      //     formikValues,
      //     eulaErr,
      //     product?.productType
      //   ),
    },
  ]
  const { activeTabInex, scrollToSection } = scrollspyTabs({
    tabs: ContainerOfferTabs,
    offset: offset,
  })

  return (
    <div className={classes.root}>
      <MainTabsV2
        activeTabIndex={activeTabInex}
        onChange={scrollToSection}
        tabs={ContainerOfferTabs}
        backGroundColor={theme.palette.textBackground.light}
        tabPosition="sticky"
        tabTop={tabTop}
      />
      {ContainerOfferTabs.map(
        containerOfferTabItem => containerOfferTabItem.renderElement
      )}
    </div>
  )
}
